import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';

/**
 * Componente para renderizar contenido HTML enriquecido
 * con soporte especial para iframes y elementos multimedia
 */
const RichTextRenderer = ({ content, className }) => {
  const containerRef = useRef(null);

  // Función para formatear URLs de video (especialmente YouTube)
  const formatVideoUrl = (url) => {
    if (!url) return '';

    // Asegurarse de que la URL comienza con http:// o https://
    if (!url.startsWith('http://') && !url.startsWith('https://')) {
      url = 'https://' + url;
    }

    // Convertir URL de YouTube normal a URL de embed
    if (url.includes('youtube.com/watch')) {
      const videoId = url.split('v=')[1]?.split('&')[0];
      if (videoId) {
        return `https://www.youtube.com/embed/${videoId}`;
      }
    }

    // Convertir URL corta de YouTube a URL de embed
    if (url.includes('youtu.be/')) {
      const videoId = url.split('youtu.be/')[1]?.split('?')[0];
      if (videoId) {
        return `https://www.youtube.com/embed/${videoId}`;
      }
    }

    // Convertir URL de Vimeo a URL de embed
    if (url.includes('vimeo.com/')) {
      const videoId = url.split('vimeo.com/')[1]?.split('?')[0];
      if (videoId) {
        return `https://player.vimeo.com/video/${videoId}`;
      }
    }

    return url;
  };

  useEffect(() => {
    if (!containerRef.current || !content) return;

    // Procesar todos los iframes dentro del contenedor
    const iframes = containerRef.current.querySelectorAll('iframe');

    iframes.forEach((iframe) => {
      // Verificar si el iframe ya está dentro de un wrapper
      if (iframe.parentNode.classList.contains('iframe-wrapper')) {
        return;
      }

      // Formatear la URL del iframe si es necesario
      const currentSrc = iframe.getAttribute('src');
      if (currentSrc) {
        const formattedSrc = formatVideoUrl(currentSrc);
        if (formattedSrc !== currentSrc) {
          iframe.setAttribute('src', formattedSrc);
        }
      }

      // Crear un contenedor para el iframe
      const wrapper = document.createElement('div');
      wrapper.className = 'iframe-wrapper';

      // Reemplazar el iframe con el wrapper
      iframe.parentNode.insertBefore(wrapper, iframe);
      wrapper.appendChild(iframe);

      // Ajustar el tamaño del wrapper según las dimensiones del iframe
      const width = iframe.getAttribute('width') || 560;
      const height = iframe.getAttribute('height') || 315;

      if (width && height) {
        const aspectRatio = (height / width) * 100;
        wrapper.style.paddingTop = `${aspectRatio}%`;
      } else {
        // Usar una relación de aspecto 16:9 por defecto
        wrapper.style.paddingTop = '56.25%';
      }

      // Asegurarse de que el iframe ocupe todo el espacio del wrapper
      iframe.style.position = 'absolute';
      iframe.style.top = '0';
      iframe.style.left = '0';
      iframe.style.width = '100%';
      iframe.style.height = '100%';
      iframe.style.border = '0';
      
      // Asegurarse de que el iframe tenga los atributos necesarios
      iframe.setAttribute('allowfullscreen', 'true');
      iframe.setAttribute('frameborder', '0');
      
      // Agregar parámetros a las URLs de YouTube para mejorar la experiencia
      if (iframe.src.includes('youtube.com/embed/')) {
        const separator = iframe.src.includes('?') ? '&' : '?';
        iframe.src = `${iframe.src}${separator}rel=0&showinfo=0&autoplay=0`;
      }
    });
  }, [content]);

  // Si no hay contenido, no renderizar nada
  if (!content) {
    return null;
  }

  return (
    <div
      ref={containerRef}
      className={className}
      dangerouslySetInnerHTML={{ __html: content }}
    />
  );
};

RichTextRenderer.propTypes = {
  content: PropTypes.string,
  className: PropTypes.string,
};

RichTextRenderer.defaultProps = {
  content: '',
  className: 'rich-text-content',
};

export default RichTextRenderer;
